import { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Sidebar } from './Sidebar.js';
import { TopBar } from './TopBar.js';
import { BottomTabBar } from './BottomTabBar.js';
import { InstallPrompt } from './InstallPrompt.js';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal.js';
import { useAuth } from '@/context/AuthContext.js';

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Keyboard shortcut listener for sequences, modal, and toggles
  useEffect(() => {
    let lastKey = '';
    let lastKeyTime = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target?.closest?.('input, textarea, select, [contenteditable="true"]') ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName || '') ||
        target?.isContentEditable
      ) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setCollapsed((prev) => !prev);
        return;
      }

      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShowShortcuts((prev) => !prev);
        return;
      }

      const now = Date.now();

      // Check sequence 'g' + <key> FIRST so that 'g t' navigates to /tasks instead of toggling theme
      if (lastKey === 'g' && now - lastKeyTime < 1000) {
        const dest = e.key.toLowerCase();
        const routes: Record<string, string> = {
          d: '/dashboard',
          m: '/my-tasks',
          p: '/projects',
          t: '/tasks',
          w: '/time',
          u: '/team',
          a: '/activity',
          r: '/reports',
          c: '/clients',
          f: '/finance',
          s: '/settings',
        };
        if (routes[dest]) {
          e.preventDefault();
          navigate(routes[dest]);
          lastKey = '';
          return;
        }
      }

      // Single-key theme toggle 't' (only when not in a sequence and no modifiers)
      if (e.key.toLowerCase() === 't' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const root = document.documentElement;
        const isDark = root.classList.toggle('dark');
        localStorage.setItem('tsos_theme', isDark ? 'dark' : 'light');
        window.dispatchEvent(new CustomEvent('tsos_theme_changed', { detail: { isDark } }));
        lastKey = '';
        return;
      }

      if (e.key.toLowerCase() === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        lastKey = 'g';
        lastKeyTime = now;
      } else {
        lastKey = '';
      }
    };

    const handleOpenCustomShortcuts = () => setShowShortcuts(true);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('tsos_open_shortcuts', handleOpenCustomShortcuts);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('tsos_open_shortcuts', handleOpenCustomShortcuts);
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-brand-surface">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-9 w-9 animate-spin rounded-full border-3 border-brand-teal border-t-transparent" />
          <span className="text-xs text-brand-muted font-medium tracking-wide">Loading workspace...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    try {
      if (location.pathname && location.pathname !== '/login') {
        sessionStorage.setItem('tsos_pre_login_path', `${location.pathname}${location.search || ''}`);
      }
    } catch {}
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-brand-surface text-brand-ink">
      {/* PWA Install Promotion Banner */}
      <InstallPrompt />

      {/* Collapsible Modern Sidebar with Mobile Slide-over Drawer */}
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      {/* Main Body Container */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0 relative">
        {/* Top Navigation Bar with Mobile Drawer Hamburger Toggle */}
        <TopBar onToggleMobile={() => setMobileOpen((prev) => !prev)} />

        {/* Main Content Area: Gutters 20px mobile, 32px tablet, 64px desktop; Extra bottom padding on mobile for tab bar */}
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 md:px-8 md:py-12 lg:px-16 lg:py-16 pb-24 md:pb-12 overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto w-full space-y-8 lg:space-y-16">
            <Outlet />
          </div>
        </main>

        {/* Mobile Floating Quick Add Button (Thumb-reachable bottom-right above tab bar) */}
        {user?.role !== 'viewer' && (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('tsos_open_quick_add'))}
            className="fixed bottom-20 right-4 z-30 h-14 w-14 rounded-full bg-brand-teal text-white shadow-xl flex items-center justify-center hover:bg-teal-700 active:scale-95 transition-transform md:hidden min-h-[52px] min-w-[52px] border-2 border-white dark:border-slate-800"
            title="Quick Add"
            aria-label="Quick Add"
          >
            <Plus className="h-6 w-6" />
          </button>
        )}

        {/* Mobile Bottom Tab Bar */}
        <BottomTabBar />
      </div>

      {/* Keyboard Shortcuts Reference Modal */}
      <KeyboardShortcutsModal isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
    </div>
  );
}

export default AppShell;
